import { ColoringBands, ColoringType } from '@/api/api';
import { Trash2, Plus } from 'lucide-react';

interface ColoringBandsFormProps {
  coloring: ColoringBands;
  onChange: (coloring: ColoringBands) => void;
  disabled?: boolean;
}

export default function ColoringBandsForm({
  coloring,
  onChange,
  disabled,
}: ColoringBandsFormProps) {
  const addBand = () => {
    const newBands: ColoringBands['bands'] = [
      ...(coloring.bands as ColoringBands['bands']),
      { startingAt: 0, color: '#000000', legend: '' },
    ];
    onChange({ ...coloring, bands: newBands });
  };

  const removeBand = (index: number) => {
    if (index === 0) {
      alert(
        'You cannot remove the first band, please change the color instead'
      );
      return;
    }
    const newBands: ColoringBands['bands'] = coloring.bands.filter(
      (_, i) => i !== index
    ) as ColoringBands['bands'];

    onChange({ ...coloring, bands: newBands });
  };

  const updateBand = (index: number, field: string, value: number | string) => {
    if (index === 0) {
      alert(
        'You cannot remove the first band, please change the color instead'
      );
      return;
    }
    const newBands = [...coloring.bands];
    newBands[index] = { ...newBands[index], [field]: value };
    onChange({ ...coloring, bands: newBands as ColoringBands['bands'] });
  };

  return (
    <div className="space-y-3 bg-green-50 p-3 rounded">
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-xs font-medium">Bands</label>
          <button
            type="button"
            onClick={addBand}
            disabled={disabled}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            Add Band
          </button>
        </div>

        <div className="space-y-2">
          {coloring.bands.map((band, index) => (
            <div key={index} className="bg-white p-2 rounded border space-y-2">
              <div className="flex gap-2">
                {index > 0 && (
                  <div className="w-24">
                    <label className="text-xs font-medium mb-1 block">
                      Starting At
                    </label>
                    <input
                      type="number"
                      value={(band as ColoringBands['bands'][1]).startingAt}
                      onChange={(e) =>
                        updateBand(
                          index,
                          'startingAt',
                          parseFloat(e.target.value)
                        )
                      }
                      disabled={disabled}
                      className="w-full px-2 py-1 border rounded text-sm"
                      step="any"
                    />
                  </div>
                )}
                <div className="w-20">
                  <label className="text-xs font-medium mb-1 block">
                    Color
                  </label>
                  <input
                    type="color"
                    value={band.color}
                    onChange={(e) => updateBand(index, 'color', e.target.value)}
                    disabled={disabled}
                    className="w-full h-8 px-1 py-1 border rounded"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">
                    Legend
                  </label>
                  <input
                    type="text"
                    value={band.legend}
                    onChange={(e) =>
                      updateBand(index, 'legend', e.target.value)
                    }
                    disabled={disabled}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Label"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeBand(index)}
                    disabled={disabled}
                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {coloring.bands.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            Note: First band has no "Starting At" value (represents minimum)
          </p>
        )}
      </div>
    </div>
  );
}
